import React, { memo, Fragment } from "react";
import Link from "@qlik/qlik/src/components/Link";
import Media from "@qlik/qlik/src/components/Media";
import { handleKeyboardEventBehavior, internalUrl } from "@qlik/qlik/src/lib/util/helpers";
import { RESOURCE_LIBRARY_PATHNAME } from "@qlik/qlik/gatsbyNode/constants";
import { getLangPrefixedPath } from "@qlik/utils/pathPrefix";

/**
 * SliderCard component
 * @param {Object} card - The card object containing card details
 * @param {String} className - The class name for the card
 * @param {Function} onClick - The function to be executed on click
 * @param {Function} onMouseEnter - The function to be executed on mouse enter
 * @param {Function} onMouseLeave - The function to be executed on mouse leave
 * @param {String} type - The type of the card (default is 'mobile')
 */
const SliderCard = memo(
    ({ card, className = "", onClick, onMouseEnter, onMouseLeave, type = "mobile", node_locale = "en" }) => {
        const { title, subtitle, description } = card ?? {};
        // Check if the card type is mobile
        const isMobile = type === "mobile";

        // Handler for mouse enter event
        const onMouseEnterHandler = () => {
            if (onMouseEnter) onMouseEnter();
        };

        // Handler for mouse leave event
        const onMouseLeaveHandler = () => {
            if (onMouseLeave) onMouseLeave();
        };

        // Get the logo and image from the card media
        const logo = card.media?.[1];
        const image = card.media?.[0];

        // Define the card CTA
        const cardCta = card.cta?.[0] ?? false;

        // Define where the card will link to
        let cardLink =
            cardCta && cardCta?.externalUrl
                ? cardCta?.externalUrl
                : internalUrl(cardCta?.internalLink, cardCta?.queryParams);

        return (
            // Card container
            <div
                className={`bg-white h-full relative overflow-hidden group shadow-xs rounded ${className} ${
                    isMobile
                        ? ""
                        : "lg:h-[499px] xl:h-[519px] lg:w-70 xl:w-87.5 transition-all duration-700 ease-out lg:hover:w-125 xl:hover:w-157.5"
                }`}
                onClick={onClick}
                onMouseEnter={onMouseEnterHandler}
                onMouseLeave={onMouseLeaveHandler}
                role="button"
                tabIndex={-1}
                onKeyDown={(e) => {
                    handleKeyboardEventBehavior(e, onClick);
                }}
            >
                {/* Card content */}
                <div
                    className={`p-7.5 xl:p-10 relative z-1 ${
                        isMobile
                            ? ""
                            : "group-hover:flex flex-col group-hover:h-full lg:group-hover:w-125 xl:group-hover:w-157.5"
                    }`}
                >
                    {/* Card subtitle */}
                    {subtitle && (
                        <p
                            className={`leading-none tracking-wide1 uppercase text-ocean font-bold text-xs mb-4 sm:text-base-1 sm:-tracking-wide3 sm:mb-5 ${
                                isMobile
                                    ? ""
                                    : "transition-colors duration-700 ease-out group-hover:text-white group-hover:text-base-1"
                            }`}
                        >
                            {subtitle}
                        </p>
                    )}
                    {/* Card title */}
                    {title && (
                        <p
                            className={`text-blue lg:text-gray text-md font-semibold leading-tight tracking-tight3 h-22 line-clamp-4 mb-5 sm:text-xl sm:h-[104px] sm:tracking-normal ${
                                isMobile
                                    ? ""
                                    : "whitespace-pre-wrap lg:w-55 xl:w-67.5 lg:group-hover:w-full lg:group-hover:mb-4.75  transition-colors duration-700 ease-out group-hover:text-white group-hover:text-5xl lg:group-hover:tracking-tight1-l lg:group-hover:h-auto"
                            }`}
                        >
                            {title}
                        </p>
                    )}
                    {/* Card description and links for non-mobile devices */}
                    {!isMobile && (
                        <div className="transition-opacity duration-700 mb-0 opacity-0 h-0 overflow-hidden group-hover:min-h-[129px] gap-3 flex flex-col justify-between group-hover:h-auto group-hover:mb-3  group-hover:opacity-100">
                            {description && (
                                <p className="line-clamp-5 text-white text-md tracking-tight4">
                                    {description.description}
                                </p>
                            )}
                            <div className="flex flex-wrap items-center">
                                {cardCta && cardLink && (
                                    <Link
                                        className="absolute top-0 left-0 w-full h-full"
                                        data-test={cardLink}
                                        to={cardLink}
                                        newWindow={cardCta?.browsingContext === "New Tab"}
                                    />
                                )}
                                {cardCta?.text && (
                                    <Fragment>
                                        <span className="text-sm font-bold text-white transition-colors group/link hover:no-underline leading-none tracking-normal">
                                            {cardCta.text}
                                        </span>
                                        <i className="arrow-white inline-block group-hover/link:translate-x-1 transition-transform ml-2 duration-200" />
                                    </Fragment>
                                )}
                            </div>
                        </div>
                    )}
                    {/* Card logo */}
                    {logo && (
                        <Media
                            media={logo}
                            className={`transition-all duration-700 object-scale-down h-10 w-full object-left-bottom lg:h-15 pointer-events-none ${
                                isMobile
                                    ? ""
                                    : "group-hover:h-[69px] max-w-[208px] group-hover:mt-auto group-hover:filter-invert group-hover:object-contain"
                            }`}
                        />
                    )}
                </div>
                {/* Card image */}
                {image && (
                    <Media
                        media={image}
                        className={`w-full rounded-b object-cover h-40 lg:h-55 ${isMobile ? "" : "group-hover:hidden"}`}
                    />
                )}
                {/* Card background image for non-mobile devices */}
                {!isMobile && (
                    <div
                        style={{
                            "--image-url":
                                card?.media?.length > 0
                                    ? `url(${card?.media?.[0]?.desktopMedia?.[0]?.secure_url})`
                                    : "none",
                        }}
                        className="hidden rounded blend-background group-hover:block absolute inset-0"
                    />
                )}
            </div>
        );
    }
);

export default SliderCard;
